import api from '../../api';
import useHttpData from '../useHttpData';

// Member health, worst first. A member with no health report is `null`: the health job
// only writes while a device is in service, so an absent report is "not reporting", never
// "healthy".
const HEALTH_RANK = { CRITICAL: 0, WARNING: 1, HEALTHY: 2 };

/**
 * The battery's health, read off its members: the worst reporting member wins, and a
 * battery none of whose members report is `null`.
 *
 * @param {Array<{healthStatus: string|null}>} members
 * @returns {'CRITICAL'|'WARNING'|'HEALTHY'|null}
 */
export const worstHealth = members => {
    let worst = null;
    for (const member of members) {
        const status = member.healthStatus;
        if (status == null || HEALTH_RANK[status] === undefined) {
            continue;
        }
        if (worst === null || HEALTH_RANK[status] < HEALTH_RANK[worst]) {
            worst = status;
        }
    }
    return worst;
};

/**
 * Group the flat `/batteries/devices` rows (one per battery member, or one row with a null
 * `device_id` for a battery with no members) into one record per battery.
 *
 * @param {Array<object>} rows the `data` array of `GET /batteries/devices`
 * @returns {Array<{id: string, name: string, priceArea: string|null, members: Array<object>, health: string|null}>}
 */
export const groupBatteries = rows => {
    const byId = new Map();
    for (const row of rows) {
        let battery = byId.get(row.battery_id);
        if (!battery) {
            battery = {
                id: row.battery_id,
                name: row.battery_name || '(unnamed)',
                priceArea: row.price_area ?? null,
                members: [],
            };
            byId.set(row.battery_id, battery);
        }
        if (row.device_id) {
            battery.members.push({
                deviceId: row.device_id,
                type: row.device_type ?? null,
                manufacturer: row.manufacturer ?? null,
                healthStatus: row.device_health_status ?? null,
                // Registered (prequalified) capacities per market, MW; null on a gateway
                // from before the field, which hides the capacity table rather than
                // reading as "nothing registered".
                marketCapacities: row.market_capacities ?? null,
            });
        }
    }
    return [...byId.values()].map(battery => ({ ...battery, health: worstHealth(battery.members) }));
};

// One record per virtual battery of the logged-in retailer, with its members and their
// latest health status.
const useRetailerBatteries = () => {
    const { data, isReady, error } = useHttpData(() => api.get_vbatt_devices().then(res => groupBatteries(res?.data ?? [])), [], []);
    return { batteries: data ?? [], isReady, error };
};

export default useRetailerBatteries;
